import bbox from '@turf/bbox';
import buffer from '@turf/buffer';
import { flame } from '../../../components/shared/vector-images';
import { telemetry } from '../../backend';
import { hasFeatureEnabled } from '../../feature';
import { boundsOutsideViewableArea } from '../../geofencing';
import { definitions, subscribe } from '../../message-broker';
import { getStore } from '../../store';
import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
  layerVisible,
} from '../_helpers';

const DYNAMIC_HEATMAP_LAYER_NAME = 'dynamic-activity-heatmap';
const DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME =
  'dynamic-activity-no-structures-heatmap';
const HEATMAP_STRUCTURE_CACHE: { [key: string]: boolean } = {};

interface LayerConfig {
  id: string;
  [key: string]: any;
}
type LatOrLon = string | number;
type Map = any;

const getLatLonCacheKey = (latitude: LatOrLon, longitude: LatOrLon) =>
  `${latitude}:${longitude}`;

const showHideActivityHeatmapLayer = (
  map: Map,
  layerName: string,
  shown: boolean,
) => {
  try {
    if (shown) {
      if (map.getLayoutProperty(layerName, 'visibility') !== 'visible') {
        telemetry(
          telemetry.DEBUG,
          `[activity heatmap mapLayer] showing layer ${layerName}`,
        );
        map.setLayoutProperty(layerName, 'visibility', 'visible');
      }
    } else if (map.getLayoutProperty(layerName, 'visibility') !== 'none') {
      telemetry(
        telemetry.DEBUG,
        `[activity heatmap mapLayer] hiding layer ${layerName}`,
      );
      map.setLayoutProperty(layerName, 'visibility', 'none');
    }
  } catch (error) {
    telemetry(
      telemetry.ERROR,
      `[activity heatmap mapLayer] Failed to toggle ${layerName}`,
    );
  }
};

// Given a latitude and longitude, create a geojson feature which is a square around the given point, adding a
// 10 meter radius
const createBoundingBox = (latitude: LatOrLon, longitude: LatOrLon) => {
  const b = buffer(
    {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          properties: {},
          geometry: {
            coordinates: [longitude, latitude],
            type: 'Point',
          },
        },
      ],
    },
    20,
    { units: 'meters' },
  );
  return bbox(b);
};

let isShown: boolean;

/**
 * Build and apply heat map styles for a given heatmap layer
 */
const applyHeatmapLayer = (map: Map, config: LayerConfig, colour?: string) => {
  if (config.id && layerExists(map, config.id)) {
    map.removeLayer(config.id);
  }

  // Solves an issue where the AIS layer doesn't exist when trying to add the heatmap
  const addUnderneath = layerExists(map, 'ais') ? 'ais' : undefined;

  map.addLayer(
    {
      type: 'heatmap',
      paint: {
        'heatmap-color': [
          'interpolate',
          ['linear'],
          ['heatmap-density'],
          0,
          colour || 'rgba(255, 92, 57, 0.0001)',
          1,
          '#FF3700',
        ],
        'heatmap-intensity': 0,
        'heatmap-weight': ['get', 'count'],
        'heatmap-radius': [
          'interpolate',
          ['linear'],
          ['zoom'],
          // First number is the zoom level, the second is the radius used (in pairs)
          4,
          7,
          11,
          7,
          12,
          10,
          13,
          25,
          14,
          50,
          15,
          100,
          16,
          200,
          16.5,
          300,
          17,
          400,
        ],
        'heatmap-opacity': 0.3,
      },
      maxzoom: 17,
      layout: { visibility: 'none' },

      ...config,
    },
    // This is the important part of this example: the addLayer
    // method takes 2 arguments: the layer as an object, and a string
    // representing another layer's name. If the other layer
    // exists in the style already, the new layer will be positioned
    // right before that layer in the stack, making it possible to put
    // 'overlays' anywhere in the layer stack.
    // Insert the layer beneath the first symbol layer.
    addUnderneath,
  );
};

/**
 * Add the appropriate heatmap layers to the map
 */
const addHeatmapLayers = (map: Map) => {
  // Add the custom source for heatmaps with structures stripped out
  if (!sourceExists(map, DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME)) {
    map.addSource(DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!layerExists(map, DYNAMIC_HEATMAP_LAYER_NAME)) {
    applyHeatmapLayer(map, {
      id: DYNAMIC_HEATMAP_LAYER_NAME,
      source: 'deckee-static',
      'source-layer': 'activity-heatmap',
    });
  }

  if (!layerExists(map, DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME)) {
    applyHeatmapLayer(map, {
      id: DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME,
      source: DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME,
    });
  }
};

/**
 * Apply the updates for the given heatmap type
 */
const applyLayerUpdates = (map: Map, dynamicHeatmapsLayerName: string) => {
  if (layerExists(map, dynamicHeatmapsLayerName)) {
    // If you don't provide a bounding box, it defaults to use the current viewport
    let activityWeightsInViewport = map.queryRenderedFeatures({
      layers: ['activity-heatmap-points'],
    });

    // Check to see if the applied heatmap layer is the one which removed bridges. If so, then apply the filtering
    if (dynamicHeatmapsLayerName === DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME) {
      const roadOrStructureAtPoint = (
        latitude: LatOrLon,
        longitude: LatOrLon,
      ) => {
        const key = getLatLonCacheKey(latitude, longitude);
        const cacheValue = HEATMAP_STRUCTURE_CACHE[key];
        if (cacheValue) {
          return cacheValue;
        }

        const box = createBoundingBox(latitude, longitude);
        const southWest = [box[0], box[1]];
        const northEast = [box[2], box[3]];
        const northEastPointPixel = map.project(northEast);
        const southWestPointPixel = map.project(southWest);

        const existingFeatures = map.queryRenderedFeatures([
          southWestPointPixel,
          northEastPointPixel,
        ]);

        const hasRoad = existingFeatures.find((f: any) =>
          ['road', 'structure', 'landuse'].includes(f.sourceLayer),
        );

        HEATMAP_STRUCTURE_CACHE[key] = !!hasRoad;

        return !!hasRoad;
      };

      activityWeightsInViewport = activityWeightsInViewport
        .filter(
          (item: any) =>
            !roadOrStructureAtPoint(
              item.geometry.coordinates[1],
              item.geometry.coordinates[0],
            ),
        )
        .map((item: any) => ({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: item.geometry.coordinates,
          },
          properties: item.properties,
        }));

      const geojson = {
        type: 'FeatureCollection',
        features: activityWeightsInViewport,
      };

      setDataSource(map, DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME, geojson);
    }

    const activityCountsInViewport = activityWeightsInViewport.map(
      (o: any) => o.properties.count,
    );

    // Only calculate how to show the heatmap intensity if there are activity counts in viewport
    if (activityCountsInViewport.length) {
      const minimumCountInViewport = Math.min(...activityCountsInViewport);
      const maximumCountInViewport = Math.max(...activityCountsInViewport);
      const getStandardDeviation = (array: number[]) => {
        const n = array.length;

        const mean = array.reduce((a, b) => a + b) / n;
        return Math.sqrt(
          array.map((x) => (x - mean) ** 2).reduce((a, b) => a + b) / n,
        );
      };
      const stdCountInViewport = getStandardDeviation(activityCountsInViewport);
      map.setPaintProperty(dynamicHeatmapsLayerName, 'heatmap-intensity', [
        'interpolate',
        ['linear'],
        ['zoom'],
        4,
        minimumCountInViewport / stdCountInViewport,
        20,
        minimumCountInViewport / maximumCountInViewport,
      ]);
    }

    const bounds = map.getBounds();
    const { lng: left, lat: top } = bounds.getNorthWest().wrap();
    const { lng: right, lat: bottom } = bounds.getSouthEast().wrap();
    const viewableMapBounds = { top, right, bottom, left };

    const outsideWaBounds = boundsOutsideViewableArea(
      'WESTERNAUSTRALIA',
      viewableMapBounds,
    );

    const { hidingAllLayers } = getStore().getState().appControls;
    showHideActivityHeatmapLayer(
      map,
      dynamicHeatmapsLayerName,
      isShown && outsideWaBounds && !hidingAllLayers,
    );
  }
};

/**
 * Get which heatmap layer should be used
 */
const getActiveHeatmapLayerName = () => {
  const useHeatmaps2 = hasFeatureEnabled('heatmaps-2.0');

  if (useHeatmaps2) {
    return DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME;
  }
  return DYNAMIC_HEATMAP_LAYER_NAME;
};

/**
 * Run an update on the heatmap layers
 */
const updateLayers = (map: Map) => {
  // The heatmap layers are all based off the activity-heatmap source, which is hidden in map-tile-style. But this needs to exist
  // in order for the other layers to be generated correctly
  if (!sourceExists(map, 'activity-heatmap')) {
    // Make sure the heatmap layers are added to the map
    addHeatmapLayers(map);

    // Calculate the heatmaps and apply styles
    // Only proceed is layer is visible
    if (!layerVisible(map, getActiveHeatmapLayerName())) return;
    applyLayerUpdates(map, getActiveHeatmapLayerName());
  }
};

const layer = {
  displayName: 'Heatmaps',
  synonyms: '',
  group: 'mapboxLayer',
  typeUrl: 'dynamic-activity-heatmap',
  // Need an empty on initialise so the bootstrap doesn't fail
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-empty-function
  onInitialize: () => {},
  icon: {
    baseShape: flame,
    iconColor: '#fff',
    markerColor: '#fff',
  },
  shownByDefault: true,
  relativeDepth: 20,
  menuOptions: {
    visible: true,
    owningMenu: 'activity-heatmap',
    order: 1,
  },
  onToggle: (map: Map, show: boolean): void => {
    const currentlyVisible = layerVisible(map, getActiveHeatmapLayerName());
    if (show && !currentlyVisible) {
      updateLayers(map);
    }
    toggleLayerIfRequired(map, getActiveHeatmapLayerName(), show);
    isShown = show;
  },
  onMapUpdateEvent: updateLayers,
};

subscribe(definitions.settings.featureChange, () => {
  const map = deckeeMap as Map;

  // Hide both types of heatmaps so we can re-apply the correct one
  showHideActivityHeatmapLayer(map, DYNAMIC_HEATMAP_LAYER_NAME, false);
  showHideActivityHeatmapLayer(
    map,
    DYNAMIC_HEATMAP_NO_STRUCTURES_LAYER_NAME,
    false,
  );

  updateLayers(map);
});

export default layer;
